/**
 * Per-slide content placeholder text — Clinq pitch deck methodology.
 *
 * Shown as the native `placeholder` attribute on the slide-content textarea
 * (iter51) so first-time founders get concrete, methodology-grounded
 * suggestions of what to put in each slide. The placeholder disappears the
 * moment the founder starts typing (native browser behaviour) — nothing is
 * saved to the deck.
 *
 * Trigger conditions (owned by the caller in PitchEditor.jsx):
 *   1. slide.content is empty
 *   2. "Notes for AI" (extraNotes) is empty
 *   3. slide has no drafts_history — i.e. truly a fresh slide
 * If ANY of those are false, the caller falls back to the plain generic
 * placeholder ("- Specific fact with a number …") so founders who deleted
 * their content don't get patronised.
 *
 * Sub-slides deliberately share the generic placeholder — founders picking
 * a sub-slide are usually extending an already-drafted parent, so per-slide
 * methodology hints would feel redundant. Only PARENT slides (num 1-12) get
 * bespoke placeholders here.
 *
 * Methodology source: `/app/backend/pitch_calibration.py` (SLIDES list) +
 * `/app/backend/pitch_ai.py` (SYSTEM_PROMPT slide-by-slide directives).
 */

// Written as arrays so we can join with "\n" without escape-hell. Each line
// starts with "- " so the placeholder VISUALLY mirrors the bullet format
// founders will actually type into the textarea.
const SLIDE_PLACEHOLDERS = {
  1: [
    "- One-line description of what you do and for whom (e.g. \"Clinq — the fastest way for African SMEs to raise capital\")",
    "- The stage you're currently raising: pre-seed · seed · Series A · debt facility",
    "- Your logo — upload it via the Photos section for a polished cover",
    "- Optional: your headquarters city and founding year",
  ],
  2: [
    "- Describe the specific problem you're solving in ONE concrete sentence — no jargon",
    "- Quantify the scale (e.g. \"62% of Kenyan SMEs cite 'access to capital' as the #1 barrier — CBK 2024\")",
    "- Why is the problem urgent NOW? Regulatory shift, market change, tech unlock",
    "- Cite an authoritative source per claim (World Bank, IFC, McKinsey, KNBS, Central Bank)",
  ],
  3: [
    "- One-sentence description of what your product does and how it works",
    "- Break down 2-3 core capabilities that differentiate you (e.g. \"AI-driven credit scoring\", \"sub-24hr disbursement\")",
    "- Your \"unfair advantage\" — the moat competitors can't replicate in <12 months",
    "- Optional: name a marquee customer or design partner using it today",
  ],
  4: [
    "- TAM — Total Addressable Market with a number AND a source (e.g. \"~$45B — IFC 2024\")",
    "- SAM — the slice you can realistically reach with your current channels + product",
    "- SOM — what you can capture in the next 3 years (usually 5-15% of SAM)",
    "- Every figure needs a citation — funders trust numbers with sources",
  ],
  5: [
    "- Revenue streams: transaction fees, subscription, licensing, take-rate, marketplace commission",
    "- Unit economics with real numbers: LTV, CAC, gross margin %, payback period in months",
    "- Pricing tiers or price points (e.g. \"$29 / seat / month\", \"3% of GMV\")",
    "- Why the model scales as volume grows (network effects, marginal cost trends)",
  ],
  6: [
    "- Key metrics with numbers: users, revenue, GMV, retention, NPS, activation rate",
    "- Growth rate: MoM or QoQ with the trend (e.g. \"38% MoM revenue growth for 6 straight months\")",
    "- Logos of notable customers, partners, or pilot institutions",
    "- Any awards, press mentions, design partners, or accelerator affiliations",
  ],
  7: [
    "- Primary acquisition channel(s): organic, paid, partnerships, community, sales-led",
    "- CAC by channel and the payback timeline",
    "- Geographic + segment expansion plan for Y1, Y2, Y3 — which markets, which customer segments, in what order",
    "- Key partnerships or distribution deals that unlock scale",
  ],
  8: [
    "- List 3-5 competitors (direct AND adjacent — funders will bring up the ones you skip)",
    "- Where you win: pricing, speed, UX, integrations, geography, regulatory license",
    "- Your defensible moat: data advantage, network effects, brand, IP, exclusive partnerships",
    "- Optional: a 2×2 positioning statement (\"we're the only X that does Y\")",
  ],
  9: [
    "- Founder names + roles + 1-line credentials each (\"ex-Head of Product at X · Y years in fintech\")",
    "- Domain expertise: prior exits, previous scale (\"grew Z to $50M ARR\"), publications",
    "- Key hires beyond founders — CTO, Head of Growth, Chief Compliance Officer",
    "- Add team photos via the Photos section — a face + story funders remember",
  ],
  10: [
    "- Revenue by year for the next 3 years (Y1 · Y2 · Y3) with the single biggest driver of each",
    "- Key assumptions behind the plan: customer count × ARPU, cost per unit, churn assumption",
    "- Path to profitability — the year you turn cash-flow positive and the milestones that get you there",
    "- Sensitivity: what changes if your top 2 assumptions are off by 20%?",
  ],
  11: [
    "- How much capital you need and whether it's debt or equity (e.g. \"USD 2M equity round\")",
    "- Be specific with your ask: amount + optional valuation cap or floor",
    "- What the capital will be used for — with percentages (product 40% · GTM 30% · hires 25% · runway 5%)",
    "- Runway this gives you AND the milestones you'll hit before the next round",
  ],
  12: [
    "- A heartfelt closing message (e.g. \"Let's build the future of African SMEs together\")",
    "- Contact details: email, WhatsApp, LinkedIn, calendar link",
    "- Clear next-step CTA: \"Schedule a call\" / \"Try the product\" / \"Read our data room\"",
    "- Optional: names of intros or advisors funders can reference-check",
  ],
};

// Legacy generic placeholder — shown when the trigger conditions above are
// not met (content non-empty, draft history exists, etc.) OR when the caller
// asks for it explicitly (sub-slides).
export const GENERIC_PLACEHOLDER =
  "- Specific fact with a number (e.g. KES 2.4B addressable market — World Bank 2024)\n" +
  "- Another fact backed by data\n" +
  "- Avoid 'innovative', 'unique', 'would', 'could'";

/**
 * Return the methodology placeholder for a given slide number.
 * Falls back to GENERIC_PLACEHOLDER for any num outside 1..12.
 */
export function getSlidePlaceholder(slideNum) {
  const lines = SLIDE_PLACEHOLDERS[slideNum];
  return lines ? lines.join("\n") : GENERIC_PLACEHOLDER;
}
